import type { OptionType } from '~/features/types';
import type { ProviderSearchFilters, SubspecialtyOption } from './api';
import { localDate } from './ProviderAvailabilityOptions';

/**
 * The panel's own draft shape. The shared Select stores whole `{ label, value }`
 * options rather than bare strings, so the filters the page applies are mapped
 * back through `toFilters` and the request payload is unchanged.
 */
export type AsyncOption = { label: string; value: string | number };

/** A city/ZIP picked from `GET /geographies/search`; the coordinates travel with the option. */
export type LocationOption = AsyncOption & { latitude: number; longitude: number };

export type ProviderSearchFormValues = {
  query: string;
  employmentType: OptionType;
  providerCategory: AsyncOption | null;
  specialty: AsyncOption | null;
  subspecialties: SubspecialtyOption[];
  start: string;
  end: string;
  shiftLabel: OptionType;
  shiftType: OptionType;
  workLocation: OptionType;
  location: LocationOption | null;
  stateCodes: OptionType[];
};

/**
 * The last APPLIED search: the form values (which carry the async options' labels and the
 * location's coordinates that `filters` only has as ids), the filters they produced, and the
 * results page the employer was on.
 */
export type StoredProviderSearch = {
  values: ProviderSearchFormValues;
  filters: ProviderSearchFilters;
  page: number;
};

// Bump the version when the stored shape changes: a tab can hold an entry from an older build,
// and a field added since would otherwise restore as undefined.
const keyFor = (viewerScopeKey: string) => `hlx.providerSearch.v1:${viewerScopeKey}`;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A `{ label, value }` option; the fixed menus use '' for "Any", a looked-up option never does. */
const isOption = (value: unknown, allowBlank = false): value is AsyncOption =>
  isObject(value) &&
  typeof value.label === 'string' &&
  ((typeof value.value === 'string' && (allowBlank || value.value !== '')) ||
    (typeof value.value === 'number' && Number.isFinite(value.value)));

const isLocation = (value: unknown): value is LocationOption =>
  isObject(value) &&
  typeof value.latitude === 'number' &&
  Number.isFinite(value.latitude) &&
  typeof value.longitude === 'number' &&
  Number.isFinite(value.longitude) &&
  isOption(value);

const FIXED_MENUS = ['employmentType', 'shiftLabel', 'shiftType', 'workLocation'];

/**
 * The form values must be the options the form renders: a tab can hold a search written by an
 * older build, and a bare string in `specialty` would reach the API as `specialtyId=NaN`.
 */
const isFormValues = (values: Record<string, unknown>) =>
  FIXED_MENUS.every((name) => isOption(values[name], true)) &&
  (values.providerCategory === null || isOption(values.providerCategory)) &&
  (values.specialty === null || isOption(values.specialty)) &&
  Array.isArray(values.subspecialties) &&
  values.subspecialties.every((option) => isOption(option)) &&
  Array.isArray(values.stateCodes) &&
  values.stateCodes.every((option) => isOption(option)) &&
  (values.location === null || isLocation(values.location));

/**
 * [MCV-1950] A tab can hold a search from before the license state became a multi-select: one
 * `stateCode` option in the values and a `stateCode` string in the filters. Carry it over as a
 * one-element `stateCodes`; anything that doesn't look like that is left for the shape check
 * to reject.
 */
const migrateStateCode = (values: Record<string, unknown>, filters: Record<string, unknown>) => {
  if (values.stateCodes === undefined && isOption(values.stateCode, true)) {
    values.stateCodes = values.stateCode.value === '' ? [] : [values.stateCode];
  }
  if (filters.stateCodes === undefined && typeof filters.stateCode === 'string') {
    filters.stateCodes = filters.stateCode ? [filters.stateCode.toUpperCase()] : undefined;
  }
  delete values.stateCode;
  delete filters.stateCode;
};

/** Null unless `parsed` has the shape this module wrote. */
const asStoredSearch = (parsed: unknown): StoredProviderSearch | null => {
  if (!isObject(parsed)) return null;
  const { values, filters, page } = parsed;
  if (!isObject(values) || !isObject(filters)) return null;
  if (typeof filters.start !== 'string') return null;
  migrateStateCode(values, filters);
  if (typeof page !== 'number' || !Number.isInteger(page) || page < 1) return null;
  if (!isFormValues(values)) return null;
  // Written together, so a filter the form no longer shows means the pair was tampered with.
  if (filters.specialtyId !== undefined && values.specialty === null) return null;
  if (filters.nearLat !== undefined && values.location === null) return null;
  return parsed as unknown as StoredProviderSearch;
};

/**
 * [MCV-1882] Writes the applied search to sessionStorage, keyed by viewer so another user or
 * role in the same tab never sees it. The page unmounts when an employer opens a result's
 * masked vault, so state held in the component alone sent them back to blank filters. Per tab
 * and gone with it, like the pending signup role.
 */
export const writeSearch = (viewerScopeKey: string, search: StoredProviderSearch): void => {
  try {
    sessionStorage.setItem(keyFor(viewerScopeKey), JSON.stringify(search));
  } catch {
    // Storage can be unavailable or full. The cost is blank filters on return, which is
    // what the page did before it remembered anything.
  }
};

export const clearSearch = (viewerScopeKey: string): void => {
  try {
    sessionStorage.removeItem(keyFor(viewerScopeKey));
  } catch {
    // See writeSearch.
  }
};

/**
 * The stored search for this viewer, or null if absent, unparseable or malformed. One whose
 * start is before today is dropped too: the search API and the form both reject a past start,
 * so restoring it would greet the employer with an error.
 */
export const readSearch = (viewerScopeKey: string): StoredProviderSearch | null => {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(keyFor(viewerScopeKey));
  } catch {
    return null;
  }
  if (!stored) return null;

  let parsed: StoredProviderSearch | null = null;
  try {
    parsed = asStoredSearch(JSON.parse(stored));
  } catch {
    // Falls through to the discard below.
  }
  if (!parsed || parsed.filters.start < localDate(new Date())) {
    clearSearch(viewerScopeKey);
    return null;
  }
  return parsed;
};
